// Everyone else: snapshots are rebuilt from the baseline the client acknowledged, then each entity is drawn
// slightly in the past and smoothly interpolated between two known positions.
import { SIM_HZ, SNAPSHOT_EVERY, SUB } from '../../../shared/constants.js';
import type { EntityInfo, EntityState, ServerMsg } from '../../../shared/protocol.js';

type Snap = Extract<ServerMsg, { type: 'snap' }>;

export interface Remote {
  id: number;
  info: EntityInfo | null;
  buf: { tick: number; s: EntityState }[];
  goneAt: number;          // server tick it left view, 0 if present
  bubble: { text: string; until: number; kind: 'say' | 'think' } | null;
}

export interface Pose { x: number; y: number; dir: number; anim: number; t: number; z: number; m: number }

const KEEP = 64;

export class RemoteWorld {
  readonly remotes = new Map<number, Remote>();
  readonly infos = new Map<number, EntityInfo>();
  private store = new Map<number, Map<number, EntityState>>();
  lastSnapId = 0;
  missingBase = 0;
  // server clock estimate (in ticks), and the interpolation delay
  private clock = 0; private clockSet = false;
  private arrivals: number[] = [];
  delayTicks = SNAPSHOT_EVERY * 2;
  snapsPerSec = 0; private snapCount = 0; private snapT = performance.now();

  constructor(private readonly selfId: () => number) {}

  addInfo(list: EntityInfo[]): void {
    for (const i of list) { this.infos.set(i.id, i); const r = this.remotes.get(i.id); if (r) r.info = i; }
  }

  /** Rebuild the full view from the acknowledged baseline plus this delta. Returns false if the baseline is unknown. */
  applySnapshot(m: Snap): boolean {
    if (m.id <= this.lastSnapId) return true;   // old or duplicate: harmless to skip
    const base = m.base ? this.store.get(m.base) : new Map<number, EntityState>();
    if (!base) { this.missingBase++; return false; }
    const cur = new Map(base);
    for (const e of m.ents) cur.set(e.id, e);
    for (const id of m.gone) cur.delete(id);
    this.store.set(m.id, cur);
    for (const id of this.store.keys()) if (id < m.id - KEEP) this.store.delete(id);
    this.lastSnapId = m.id;
    this.observeTick(m.tick);
    const me = this.selfId();
    for (const [id, s] of cur) {
      if (id === me) continue;
      let r = this.remotes.get(id);
      if (!r) { r = { id, info: this.infos.get(id) ?? null, buf: [], goneAt: 0, bubble: null }; this.remotes.set(id, r); }
      r.goneAt = 0;
      const last = r.buf[r.buf.length - 1];
      if (!last || last.tick < m.tick) r.buf.push({ tick: m.tick, s });
      if (r.buf.length > 40) r.buf.shift();
    }
    for (const r of this.remotes.values()) if (!cur.has(r.id) && !r.goneAt) r.goneAt = m.tick;
    this.snapCount++;
    return true;
  }

  private observeTick(tick: number): void {
    if (!this.clockSet || Math.abs(tick - this.clock) > 60) { this.clock = tick; this.clockSet = true; this.arrivals = []; return; }
    const err = tick - this.clock;
    this.arrivals.push(err); if (this.arrivals.length > 40) this.arrivals.shift();
    this.clock += err * 0.08;   // drift gently toward what we observe
    // jitter: spread of arrival errors; the delay leaves room for it so we rarely run out of data
    const mean = this.arrivals.reduce((a, b) => a + b, 0) / this.arrivals.length;
    const sd = Math.sqrt(this.arrivals.reduce((a, b) => a + (b - mean) ** 2, 0) / this.arrivals.length);
    const target = Math.min(36, Math.max(SNAPSHOT_EVERY * 2, SNAPSHOT_EVERY + 2.5 * sd + 2));
    this.delayTicks += (target - this.delayTicks) * 0.05;
  }

  /** Advance the clock by real time. */
  advance(dt: number): void {
    if (this.clockSet) this.clock += dt * SIM_HZ;
    const now = performance.now();
    if (now - this.snapT >= 1000) { this.snapsPerSec = this.snapCount * 1000 / (now - this.snapT); this.snapCount = 0; this.snapT = now; }
    const renderTick = this.renderTick;
    for (const [id, r] of this.remotes) if (r.goneAt && renderTick > r.goneAt + 2) this.remotes.delete(id);
  }

  get renderTick(): number { return this.clock - this.delayTicks; }

  /** Interpolated pose in pixels at the current render time. */
  pose(r: Remote): Pose | null {
    const b = r.buf; if (!b.length) return null;
    const rt = this.renderTick;
    let i = b.length - 1;
    while (i > 0 && b[i - 1].tick > rt) i--;
    const hi = b[i], lo = i > 0 ? b[i - 1] : hi;
    if (rt >= hi.tick || lo === hi) {   // ran past the newest data: hold (never guess far ahead)
      const s = hi.s; return { x: s.x / SUB, y: s.y / SUB, dir: s.dir, anim: s.anim, t: s.t + Math.max(0, Math.min(6, rt - hi.tick)), z: (s.z ?? 0) / SUB, m: s.m ?? 0 };
    }
    const k = (rt - lo.tick) / (hi.tick - lo.tick), a = lo.s, c = hi.s;
    while (b.length > 2 && b[1].tick < rt - SIM_HZ) b.shift();
    const az = a.z ?? 0, cz = c.z ?? 0;
    return { x: (a.x + (c.x - a.x) * k) / SUB, y: (a.y + (c.y - a.y) * k) / SUB, dir: k < 0.5 ? a.dir : c.dir, anim: k < 0.5 ? a.anim : c.anim, t: a.t + (rt - lo.tick), z: (az + (cz - az) * k) / SUB, m: (k < 0.5 ? a.m : c.m) ?? 0 };
  }

  reset(): void { this.remotes.clear(); this.store.clear(); this.lastSnapId = 0; this.clockSet = false; }
}
